import qs from "qs";
import {all, call, put, takeLatest} from "redux-saga/effects";
import {getAppLogins, getTokenFromCode, whoami} from "../../api/auth";
import {appConfig, setLoginOptions} from "../../config/defaultConfig";
import {
  getAuthToken,
  setAuthToken,
  setSessionUser
} from "../../config/localStorage";
import {clearSession} from "../../utils/authUtils";
import {
  authCodeReceived,
  authFlowComplete,
  authFlowStart,
  authIdpLogin,
  authIdpLogoutSuccess,
  authTokenFailed,
  authTokenReceived,
  authTokenRequested,
  authUserFailed,
  authUserReceived,
  authUserRequested
} from "../features/authSlice";

const {IROH_SERVICES, ROUTES} = appConfig.APP;

// Step 1A: We check to see if the user has a cached auth token.
function* authFlowSaga() {
  const token = getAuthToken();
  const {code} = qs.parse(window.location.search, {ignoreQueryPrefix: true});

  if (token) {
    // User has logged in previously. Go to Step 2.
    yield put(authTokenReceived(token));
  } else if (code) {
    // User is currently logging in. Go to Step 1B
    yield put(authCodeReceived({code}));
  } else {
    // User was previously logged out. Push them to login.
    yield put(authTokenFailed());
  }
}

// Step 1B: If the user currently logging in, we exchange their auth code for an auth token.
function* fetchTokenSaga({payload: {code}}) {
  yield put(authTokenRequested());
  try {
    const {
      data: {access_token}
    } = yield call(getTokenFromCode, code);
    yield call(setAuthToken, access_token);
    yield put(authTokenReceived(access_token));
  } catch (error) {
    console.error(error);
    yield put(authTokenFailed(error));
  }
}

// Step 2: We use the auth token to verify the user's identity (authentication and authorization).
function* fetchUserSaga() {
  yield put(authUserRequested());
  try {
    const {data: me} = yield call(whoami);
    const {data: logins} = yield call(getAppLogins);
    setSessionUser(me);
    setLoginOptions(logins);
    // extends idp with name and relevant urls
    me.idp = {...me.idp, ...logins[me.idp.id]};
    yield put(authUserReceived(me));
  } catch (error) {
    console.error(error);
    yield put(authUserFailed(error));
  }
}

// Step 3: Once the user is authed, we do some cleanup on the URL.
function* loginCleanupSaga() {
  const {code, ...otherSearch} = qs.parse(window.location.search, {
    ignoreQueryPrefix: true
  });

  // The user just logged in
  if (code) {
    // Remove the `code` param, keep other params intact
    const {origin, pathname, hash} = window.location;
    const newSearch = qs.stringify(otherSearch, {addQueryPrefix: true});
    window.history.replaceState(
      {},
      document.title,
      origin + pathname + newSearch + hash
    );
  }

  // Signal that the auth process is complete and other sagas can safely fetch data
  yield put(authFlowComplete());
}

// Pushes a user back to the iroh-auth/login page
function* irohLoginSaga() {
  const {redirect_after_login = getRedirectUrl(), ...otherSearch} = qs.parse(
    window.location.search,
    {
      ignoreQueryPrefix: true
    }
  );
  const loginUrl = `${window.CONFIG.apiBaseUri}/iroh${IROH_SERVICES.ROUTES.LOGIN}`;
  const newSearch = qs.stringify(
    {redirect_after_login, ...otherSearch},
    {addQueryPrefix: true}
  );

  yield call(window.open, loginUrl + newSearch, "_self");
}

// Clears the user's localStorage information
function* clearSessionSaga() {
  yield clearSession();
}

export default function* authSaga() {
  yield all([
    yield takeLatest(authFlowStart, authFlowSaga),
    yield takeLatest(authTokenReceived, fetchUserSaga),
    yield takeLatest(authCodeReceived, fetchTokenSaga),
    yield takeLatest([authIdpLogin, authTokenFailed], irohLoginSaga),
    yield takeLatest(authUserReceived, loginCleanupSaga),
    yield takeLatest(
      [authIdpLogoutSuccess, authTokenFailed, authUserFailed],
      clearSessionSaga
    )
  ]);
}

function getRedirectUrl() {
  if (window.location.pathname.endsWith(ROUTES.LOGOUT)) {
    return window.location.origin + window.publicURL;
  } else {
    return window.location.toString();
  }
}
